import { useCallback, useEffect, useState } from 'react';
import type { EmblaCarouselType } from 'embla-carousel';

export const useCarouselDotPagination = (emblaApi: EmblaCarouselType | undefined) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotClick = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((api: EmblaCarouselType) => {
    const nextScrollSnaps = api.scrollSnapList();
    setScrollSnaps((previousScrollSnaps) => {
      const isUnchanged =
        previousScrollSnaps.length === nextScrollSnaps.length &&
        previousScrollSnaps.every((snap, snapIndex) => snap === nextScrollSnaps[snapIndex]);
      return isUnchanged ? previousScrollSnaps : nextScrollSnaps;
    });
  }, []);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    const nextSelectedIndex = api.selectedScrollSnap();
    setSelectedIndex((previousSelectedIndex) =>
      previousSelectedIndex === nextSelectedIndex ? previousSelectedIndex : nextSelectedIndex,
    );
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);

    return () => {
      emblaApi.off('reInit', onInit).off('reInit', onSelect).off('select', onSelect);
    };
  }, [emblaApi, onInit, onSelect]);

  return { selectedIndex, scrollSnaps, onDotClick };
};
